import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Notebook,
  UsersThree,
  Sparkle,
  ChartBar,
  ArrowRight,
  SpeakerHigh,
  SpeakerSlash,
} from "@phosphor-icons/react";

const INTRO_STORAGE_KEY = "al_axe_intro_played";
const INTRO_MUTED_KEY = "al_axe_intro_muted";

const FEATURES = [
  {
    icon: Notebook,
    title: "Daily operations log",
    body: "Capture conversations, tasks, plans, interviews, wins, and lessons — with photos, timing, and outcome ratings — all in one clean daily entry.",
  },
  {
    icon: UsersThree,
    title: "People timeline",
    body: "Every mention links automatically. Click any name to see every interaction, decision, and follow-up you've had with that person.",
  },
  {
    icon: Sparkle,
    title: "Source-linked AI assistant",
    body: "Ask anything about your journal. AL-AXE answers with citations from your own entries — never invented, always grounded.",
  },
  {
    icon: ChartBar,
    title: "Reports & search",
    body: "Full-text search across every entry, plus one-click PDF and Excel reports, weekly AI summaries, and highlights of your Wins.",
  },
];

export default function Landing() {
  const { user, loading } = useAuth();

  // Intro video plays ONCE per browser session — a refresh after it plays keeps
  // the logo, but a new session (or a fresh visitor) sees it again. If autoplay
  // is blocked or the file fails, we fall back to the logo immediately.
  const initialPlayed = typeof window !== "undefined" && sessionStorage.getItem(INTRO_STORAGE_KEY) === "1";
  // Remember the visitor's audio preference across sessions (localStorage).
  // Defaults to muted so browser autoplay rules always let the first play through.
  const initialMuted = typeof window === "undefined" || localStorage.getItem(INTRO_MUTED_KEY) !== "0";
  const [introPlayed, setIntroPlayed] = useState(initialPlayed);
  const [muted, setMuted] = useState(initialMuted);
  const videoRef = useRef(null);

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !videoRef.current.muted;
    videoRef.current.muted = next;
    setMuted(next);
    try { localStorage.setItem(INTRO_MUTED_KEY, next ? "1" : "0"); } catch { /* private mode */ }
    // If autoplay was blocked because we tried unmuted, this user-gesture
    // toggle re-kicks playback.
    if (!next && videoRef.current.paused) {
      const p = videoRef.current.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* leave muted */ });
    }
  };

  useEffect(() => {
    if (introPlayed || !videoRef.current) return;
    const v = videoRef.current;
    // Kick autoplay explicitly. If it fails (usually because the user's last
    // session left sound ON and browsers only autoplay muted), fall back to
    // muted and try again — the video still plays, they can tap for sound.
    const attempt = v.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {
        v.muted = true;
        setMuted(true);
        const retry = v.play();
        if (retry && typeof retry.catch === "function") retry.catch(() => finishIntro());
      });
    }
    // Safety net: force the reveal after the known video length + a small
    // buffer, in case the `ended` event never fires (some iOS Safari quirks).
    const t = setTimeout(finishIntro, 27000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPlayed]);

  const finishIntro = () => {
    if (introPlayed) return;
    try { sessionStorage.setItem(INTRO_STORAGE_KEY, "1"); } catch { /* private mode */ }
    setIntroPlayed(true);
  };

  // Already signed in? Go straight to the app.
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div data-testid="landing-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
      {/* ---- Top nav ---- */}
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 md:px-10 py-6">
        <Link to="/" className="flex items-center" data-testid="landing-brand">
          <img src="/al-axe-brand.png" alt="AL-AXE — Intelligent Operations" className="h-9 md:h-10 w-auto" />
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/login"
            data-testid="landing-signin"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="landing-signup"
            className="text-sm font-medium al-gradient text-white px-4 py-2 rounded-full shadow-sm hover:opacity-95 transition-opacity inline-flex items-center gap-1"
          >
            Create account <ArrowRight size={14} weight="bold" />
          </Link>
        </nav>
      </header>

      {/* ---- Hero ---- */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-8 md:pt-16 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-[1.05fr,0.95fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-600 mb-4">
              For frontline leaders
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Turn everyday operations into{" "}
              <span className="al-gradient-text">usable knowledge</span>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              AL-AXE exists to give frontline leaders a clear, objective understanding of their operations — so you can identify patterns, improve processes, develop your people, and make better decisions.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="hero-signup"
                className="al-gradient text-white px-6 py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2"
              >
                Start your journal <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-signin"
                className="text-slate-700 px-6 py-3 rounded-full font-medium border border-slate-200 hover:border-slate-300 hover:bg-white transition-colors"
              >
                I already have an account
              </Link>
            </div>
          </div>

          {/* ---- Hero visual: intro video plays through once, then reveals the brand mark ---- */}
          <div className="relative order-first lg:order-none" data-testid="hero-visual">
            <div
              className={`relative rounded-3xl bg-white border border-slate-200/70 shadow-[0_30px_80px_-40px_rgba(2,6,23,0.25)] overflow-hidden transition-all duration-500 ${
                introPlayed
                  ? "p-8 sm:p-12"        // logo card sizes to its content
                  : "p-0 aspect-video"    // video fills the whole card, 16:9
              }`}
            >
              {/* Soft brand-gradient aurora behind the mark — hidden while the
                  video plays so it doesn't seep through the letterbox edges. */}
              {introPlayed && (
                <div aria-hidden className="absolute inset-0 pointer-events-none">
                  <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-teal-300/30 blur-3xl" />
                  <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-fuchsia-300/25 blur-3xl" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-indigo-200/25 blur-3xl" />
                </div>
              )}

              {/* Intro video — plays once per session */}
              {!introPlayed && (
                <video
                  ref={videoRef}
                  data-testid="landing-intro-video"
                  className="absolute inset-0 w-full h-full object-cover z-20 bg-white"
                  muted={muted}
                  playsInline
                  autoPlay
                  preload="auto"
                  onEnded={finishIntro}
                  onError={finishIntro}
                >
                  <source src="/media/al-axe-intro.webm" type="video/webm" />
                  <source src="/media/al-axe-intro.mp4" type="video/mp4" />
                </video>
              )}

              {/* Mute/unmute pill — bottom-left so it doesn't fight the Skip button */}
              {!introPlayed && (
                <button
                  type="button"
                  onClick={toggleMute}
                  data-testid="landing-intro-mute"
                  aria-label={muted ? "Unmute intro" : "Mute intro"}
                  className="absolute bottom-4 left-4 z-30 inline-flex items-center gap-1.5 text-xs font-medium bg-white/90 hover:bg-white text-slate-700 backdrop-blur-sm rounded-full px-3 py-2 md:py-1.5 shadow-sm transition-colors"
                >
                  {muted ? (
                    <><SpeakerSlash size={14} weight="fill" /> Tap for sound</>
                  ) : (
                    <><SpeakerHigh size={14} weight="fill" /> Sound on</>
                  )}
                </button>
              )}

              {/* Skip button — respects visitors who don't want to wait */}
              {!introPlayed && (
                <button
                  type="button"
                  onClick={finishIntro}
                  data-testid="landing-intro-skip"
                  className="absolute bottom-4 right-4 z-30 text-xs font-medium bg-white/90 hover:bg-white text-slate-700 backdrop-blur-sm rounded-full px-3 py-2 md:py-1.5 shadow-sm transition-colors"
                >
                  Skip intro →
                </button>
              )}

              {/* Brand logo — crossfades in when intro ends (or on session revisit) */}
              {introPlayed && (
                <img
                  src="/al-axe-brand.png"
                  alt="AL-AXE logo"
                  data-testid="landing-brand-image"
                  className="relative z-10 w-full max-w-md mx-auto block animate-[fadein_.5s_ease-out]"
                />
              )}
            </div>
            <div className="absolute -bottom-4 left-8 right-8 h-8 rounded-full bg-black/5 blur-2xl -z-10 hidden lg:block" />
          </div>
        </div>
      </section>

      {/* ---- Features ---- */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-3">
          What's inside
        </div>
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight max-w-2xl">
          Everything you need to lead better — nothing you don't.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 gap-4 md:gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-white border border-slate-200/70 rounded-2xl p-6 hover:border-teal-300 hover:shadow-[0_10px_30px_-16px_rgba(2,6,23,0.18)] transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-700 grid place-items-center mb-4">
                <f.icon size={22} weight="duotone" />
              </div>
              <div className="text-base font-semibold tracking-tight">{f.title}</div>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Closing CTA ---- */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        <div className="rounded-3xl al-gradient text-white p-8 md:p-12 shadow-lg">
          <div className="max-w-2xl">
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
              Ready to make every day count?
            </h3>
            <p className="text-white/90 mt-3 text-sm md:text-base leading-relaxed">
              Your operations journal — private, secure, and always yours. Start capturing the work that matters.
            </p>
            <Link
              to="/register"
              data-testid="closing-signup"
              className="mt-6 inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-full font-medium shadow hover:shadow-md transition-all"
            >
              Create your account <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="max-w-6xl mx-auto px-6 md:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
        <div>© {new Date().getFullYear()} AL-AXE. Built for leaders.</div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="hover:text-slate-700">Sign in</Link>
          <span aria-hidden>·</span>
          <Link to="/register" className="hover:text-slate-700">Create account</Link>
        </div>
      </footer>
    </div>
  );
}
